// Resize + compress a picked image to a reasonable size for storage in MongoDB
// (avoids hitting the 16MB BSON document limit when quotes carry several photos).
//
// Strategy:
//   - Load the file into an Image element.
//   - Draw to a canvas with the longest edge capped at MAX_EDGE.
//   - Export as JPEG at QUALITY.
// Typical phone photo (3-8 MB) → 80-250 KB data URL.

const MAX_EDGE = 1400;
const QUALITY = 0.82;

export async function compressImageFile(file) {
  if (!file) return null;
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  // SVG / GIF / very small files: skip canvas (preserves animation / vector quality).
  if (file.type === "image/svg+xml" || file.type === "image/gif" || file.size < 200 * 1024) {
    return dataUrl;
  }

  return compressDataUrl(dataUrl);
}

// Compress an existing data URL (e.g. one loaded from the database). Returns
// the original if it is already small, otherwise re-draws it via canvas.
export async function compressDataUrl(dataUrl, threshold = 400 * 1024) {
  if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) return dataUrl;
  // Skip SVG/GIF/already small.
  if (dataUrl.startsWith("data:image/svg") || dataUrl.startsWith("data:image/gif")) return dataUrl;
  if (dataUrl.length < threshold) return dataUrl;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      try {
        resolve(canvas.toDataURL("image/jpeg", QUALITY));
      } catch (_) {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}
